import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Trash2 } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Callout, Dialog, DialogContent, DialogFooter, Field, Input, Panel, Spinner, useToast } from '../../ui/index.js';

interface IntakeKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_by_name: string | null;
}

interface Payload {
  keys: IntakeKey[];
  desks: { tenant: string; desk: string; hasScope: boolean }[];
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

/**
 * Platform > Desks: the assistant's front door (079).
 *
 * ONE KEY FOR EVERY DESK THAT OPTED IN. The assistant asks Tributary, and
 * Tributary decides whose a question is. A desk opts in from its own Tenant
 * settings; this shows which have, and mints the key the assistant uses. The
 * key opens nothing else: no desk route accepts it.
 */
export function IntakeKeysPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['platform-intake-keys'], queryFn: () => api.get<Payload>('/api/v1/platform/intake-keys') });
  const [minting, setMinting] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['platform-intake-keys'] });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/platform/intake-keys/${id}`),
    onSuccess: () => {
      refresh();
      toast.success('Key revoked', 'Anything still using it is refused from now on.');
    },
    onError: (e) => toast.error('Not revoked', e instanceof ApiError ? e.message : undefined),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <p className="text-xs text-ink-tertiary">Agent intake could not be loaded.</p>;
  const live = q.data.keys.filter((k) => !k.revoked_at);

  return (
    <Panel
      title="AI agent intake"
      subtitle="Agents — the Teams bot, Claude, or any assistant connected through the MCP — ask Tributary, which decides whose a question is among the desks that take their requests."
      action={
        <Button size="xs" variant="secondary" leading={<KeyRound />} onClick={() => setMinting(true)}>
          New intake key
        </Button>
      }
    >
      <div className="space-y-3 text-xs">
        <div>
          <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">Desks taking agent requests</p>
          {q.data.desks.length === 0 ? (
            <p className="text-ink-tertiary">None yet. A desk turns on “Take requests from AI agents” under its Tenant settings.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {q.data.desks.map((d) => (
                <li key={d.tenant}>
                  <Badge tone={d.hasScope ? 'neutral' : 'warn'}>
                    {d.desk}
                    {d.hasScope ? '' : ' (no scope written)'}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">Intake keys</p>
          {live.length === 0 ? (
            <p className="text-ink-tertiary">No key. No agent can ask any desk until one is made.</p>
          ) : (
            <ul className="space-y-1.5">
              {live.map((k) => (
                <li key={k.id} className="flex items-center justify-between gap-2 rounded-md border border-line-subtle p-2">
                  <span>
                    <span className="font-medium text-ink">{k.name}</span>{' '}
                    <span className="font-mono text-ink-tertiary">{k.key_prefix}…</span>
                    <span className="block text-2xs text-ink-secondary">
                      Made {when(k.created_at)}
                      {k.created_by_name ? ` by ${k.created_by_name}` : ''} · last used {when(k.last_used_at)}
                    </span>
                  </span>
                  <Button
                    size="xs"
                    variant="ghost"
                    leading={<Trash2 />}
                    className="text-danger"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate(k.id)}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {minting && <MintDialog onClose={() => setMinting(false)} onDone={refresh} />}
    </Panel>
  );
}

function MintDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const go = useMutation({
    mutationFn: () => api.post<{ key: string }>('/api/v1/platform/intake-keys', { name }),
    onSuccess: (r) => {
      setSecret(r.key);
      onDone();
    },
    onError: (e) => toast.error('No key made', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="New intake key"
        description="A key for one agent. It can ask any desk that takes requests from AI agents, and nothing else."
      >
        {secret ? (
          <Callout tone="warn">
            <p className="mb-1.5">This is the only time it is shown. Put it in the agent's configuration now.</p>
            <code className="block break-all rounded bg-surface-sunken p-2 font-mono text-2xs">{secret}</code>
          </Callout>
        ) : (
          <Field
            label="Name"
            required
            hint="The agent that uses it. Tickets it files show this name, and the list says where to rotate it."
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Teams bot, Claude, …" />
          </Field>
        )}
        <DialogFooter>
          {secret ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => go.mutate()} loading={go.isPending} disabled={!name.trim()}>
                Make key
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
